import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Text, Box, Flex, Link, useDisclosure } from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { TCluster } from 'pages/ClusterManagement/types';
import { useGetClusterQuery } from 'core/API/clusterApi';
import { CLUSTER_LIST_FIELDS } from 'pages/ClusterManagement/const';

const EMPTY_DATA: any = [];

const useClusterContainer = () => {
    const translate = useTranslation();

    const [selectedRetailer, setSelectedRetailer] = useState<number>(0);
    const [actionModalType, setActionModalType] = useState<'add' | 'edit' | 'delete' | undefined>();
    const [currentCluster, setCurrentCluster] = useState<TCluster | null>(null);

    const { data: clusterData, isError, isFetching } = useGetClusterQuery();
    const actionModalProps = useDisclosure();

    const handleEditModal = useCallback((cluster: TCluster) => {
        setActionModalType('edit');
        setCurrentCluster(cluster);
        actionModalProps.onOpen();
    }, []);

    useEffect(() => {
        !actionModalProps.isOpen && setCurrentCluster(null);
    }, [actionModalProps.isOpen]);

    const columns: ColumnDef<TCluster, any>[] = useMemo(() => {
        const filteredTableHeader: ColumnDef<TCluster, any>[] = CLUSTER_LIST_FIELDS.map(
            (header: string): ColumnDef<TCluster, any> => {
                switch (header) {
                    case 'label':
                        return {
                            header: () => translate(`cluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return (
                                    <Flex>
                                        <Link onClick={() => handleEditModal(original)} color="blue.500">
                                            {original[header as keyof TCluster] as string}
                                        </Link>
                                    </Flex>
                                );
                            },
                        };
                    case 'color':
                        return {
                            header: () => translate(`cluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                const value = original[header as keyof TCluster] as string;
                                return (
                                    <Box display="flex">
                                        <Box
                                            w="20px"
                                            h="20px"
                                            borderRadius="50%"
                                            bg={value}
                                            border={value.toLowerCase() === '#ffffff' ? '1px solid #E2E8F0' : 'none'}
                                            mr="4px"
                                            display="inline-block"
                                        />
                                    </Box>
                                );
                            },
                        };
                    default:
                        return {
                            header: () => translate(`cluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return <Flex>{original[header as keyof TCluster] as string}</Flex>;
                            },
                        };
                }
            },
        );

        return filteredTableHeader;
    }, []);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const table = useReactTable<TCluster>({
        data: clusterData?.data || EMPTY_DATA,
        columns,
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            isLoading: isFetching,
            table,
            selectedRetailer,
            actionModalProps,
            actionModalType,
            currentCluster,
            setActionModalType,
            setSelectedRetailer,
        }),
        [isFetching, table, selectedRetailer, actionModalProps.isOpen, currentCluster, actionModalType],
    );

    return props;
};

export type TClusterViewer = ReturnType<typeof useClusterContainer>;
export default useClusterContainer;
